import type { BookingState } from '../state/types';
import { hasActiveBookingSession } from './bookingSession';

/**
 * Sentry `guard` tag value for the genuine fake-confirmation signature: a user
 * reached /booking-final with an ACTIVE booking session but no committed
 * reservation. This is the 2026-05-25 incident signal and the canary metric
 * for the skip-confirm rollout — the `booking-final-no-reservation-id` Sentry
 * alert (docs/runbooks/sentry-alerts.md) keys off this exact value.
 */
export const BOOKING_FINAL_NO_RESERVATION_TAG = 'booking-final-no-reservation-id';

/**
 * Sentry `guard` tag value for the BENIGN case: a cold-load / deep-link /
 * bookmark / search-engine landing on /booking-final with no active session
 * at all. Deliberately DISTINCT from the alert tag so these unactionable
 * events form a separate, debug-level Sentry issue and never trip the alert
 * (Cluster A1/Sub-Y noise — see docs/plans/sentry-bf-systematic-fix.md).
 */
export const BOOKING_FINAL_NO_SESSION_TAG = 'booking-final-no-session';

export interface BookingFinalGuardClassification {
  message: string;
  level: 'warning' | 'debug';
  guardTag: typeof BOOKING_FINAL_NO_RESERVATION_TAG | typeof BOOKING_FINAL_NO_SESSION_TAG;
  guardSubset: 'actionable' | 'no_active_session';
}

/**
 * Classifies a "reached /booking-final without a reservationId" guard event so
 * the actionable fake-confirmation signal stays loud (warning + alert tag)
 * while benign cold-load noise is downgraded to a separate debug-level issue.
 *
 * Mirrors the already-shipped PaymentPage guard downgrade, but discriminates
 * on booking STATE rather than `document.referrer`: for BN the downgrade would
 * suppress the very alert we rely on, so referrer (which can be empty for a
 * real direct-then-book user) is too risky here.
 */
export function classifyBookingFinalGuard(
  state: BookingState,
): BookingFinalGuardClassification {
  if (hasActiveBookingSession(state)) {
    return {
      message: 'BookingFinalPage rendered without reservationId',
      level: 'warning',
      guardTag: BOOKING_FINAL_NO_RESERVATION_TAG,
      guardSubset: 'actionable',
    };
  }

  return {
    message: 'BookingFinalPage rendered without reservationId (no active session)',
    level: 'debug',
    guardTag: BOOKING_FINAL_NO_SESSION_TAG,
    guardSubset: 'no_active_session',
  };
}
